import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import {
    Link,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom'
import { Check, Eye, EyeOff, ShieldCheck } from 'lucide-react'
import { ApiError } from '~/api/client'
import { getMe, login } from '~/api/auth'
import { useAuthStore } from '~/stores/auth-store'
import { env } from '~/env'
import { Button } from '~/components/ui/button'
import { ErrorBanner } from '~/components/ui/error-banner'
import { Input } from '~/components/ui/input'
import { cn } from '~/lib/utils'

interface LocationState {
    from?: string
}

/** location.search 또는 가드가 넘긴 state.from에서 auth_error 쿼리를 읽는다. */
function readAuthError(search: string, from?: string): string | null {
    const direct = new URLSearchParams(search).get('auth_error')
    if (direct) return direct
    const q = from?.includes('?') ? from.split('?')[1] : ''
    return q ? new URLSearchParams(q).get('auth_error') : null
}

/** 히어로 좌측의 라이브 "AI 진단" 데모 — 점검 항목이 하나씩 체크되는 루프. */
const DEMO_CHECKS = [
    '작업장 분리·청결 상태',
    '냉장·냉동 온도 관리',
    '교차오염 방지 조치',
    '기록·문서 관리',
]

function LiveInspection() {
    const [step, setStep] = useState(0)
    useEffect(() => {
        const t = setInterval(
            () => setStep((s) => (s + 1) % (DEMO_CHECKS.length + 2)),
            850
        )
        return () => clearInterval(t)
    }, [])
    const done = Math.min(step, DEMO_CHECKS.length)
    const pct = Math.round((done / DEMO_CHECKS.length) * 100)
    return (
        <div className='animate-in fade-in mt-8 w-full max-w-sm rounded-lg bg-white/10 p-5 ring-1 ring-white/20 duration-700'>
            <div className='flex items-center justify-between text-xs text-white/80'>
                <span className='inline-flex items-center gap-1.5 font-semibold'>
                    <ShieldCheck className='size-3.5' />
                    AI 진단 진행
                </span>
                <span className='font-mono tabular-nums'>{pct}%</span>
            </div>
            <div className='mt-2 h-1.5 overflow-hidden rounded-full bg-white/20'>
                <div
                    className='h-full rounded-full bg-white transition-all duration-500'
                    style={{ width: `${pct}%` }}
                />
            </div>
            <ul className='mt-4 space-y-2.5'>
                {DEMO_CHECKS.map((label, i) => {
                    const checked = i < done
                    return (
                        <li
                            key={label}
                            className='flex items-center gap-2.5 text-sm'>
                            <span
                                className={cn(
                                    'grid size-5 place-items-center rounded-full transition-all duration-300',
                                    checked
                                        ? 'text-primary bg-white'
                                        : 'ring-1 ring-white/30'
                                )}>
                                {checked ? (
                                    <Check className='size-3.5' />
                                ) : null}
                            </span>
                            <span
                                className={cn(
                                    'transition-colors',
                                    checked ? 'text-white' : 'text-white/55'
                                )}>
                                {label}
                            </span>
                        </li>
                    )
                })}
            </ul>
            {/* 완료 줄은 항상 자리를 차지하고 opacity만 전환 → 카드 높이가 튀지 않음. */}
            <p
                className={cn(
                    'mt-3 flex items-center gap-1.5 text-sm font-semibold text-white transition-opacity duration-300',
                    done === DEMO_CHECKS.length ? 'opacity-100' : 'opacity-0'
                )}>
                <Check className='size-4' /> 점검 완료 · 적합 진단
            </p>
        </div>
    )
}

/**
 * 로그인 페이지 — "디지털 정부" 스플릿 히어로.
 * - 좌측: 임팩트 있는 히어로(라이브 AI 진단 데모 + 미세 모션)
 * - 우측: 이메일/비밀번호 로그인 + knoka(OIDC)
 */
export function LoginPage() {
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const setUser = useAuthStore((s) => s.setUser)

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [showPw, setShowPw] = useState(false)
    // 히어로 마우스 패럴랙스(중심 기준 -0.5~0.5).
    const [tilt, setTilt] = useState({ x: 0, y: 0 })

    const fromTarget = (location.state as LocationState | null)?.from
    const redirectTarget = fromTarget ?? '/'
    const goAfterLogin = () => navigate(redirectTarget, { replace: true })

    // knoka 서버사이드 콜백이 계정 수동연결 필요로 앱에 되돌린 경우 안내한다.
    useEffect(() => {
        const authError = readAuthError(location.search, fromTarget)
        if (authError === 'link_required') {
            setError(
                'knock 계정과 연결된 사용자가 없어요. 먼저 이메일/비밀번호로 로그인해 계정을 연결해 주세요.'
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams])

    const handleSelfLogin = async (e: FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)
        try {
            // 백엔드가 HTTP-only 쿠키를 set → 세션 사용자 조회 후 스토어 반영.
            await login({ email, password })
            const user = await getMe()
            setUser(user)
            goAfterLogin()
        } catch (err) {
            // 403(관리자 계정/승인 대기/거부 등)은 서버 메시지를 그대로 안내한다.
            setError(
                err instanceof ApiError
                    ? err.message
                    : err instanceof Error
                      ? err.message
                      : '로그인에 실패했어요.'
            )
        } finally {
            setLoading(false)
        }
    }

    // knoka SSO는 서버사이드 흐름이라 백엔드 /auth/login으로 전체 페이지를 이동한다.
    const handleKnokaLogin = () => {
        const base = env.API_BASE_URL.replace(/\/$/, '')
        window.location.href = `${base}/auth/login`
    }

    return (
        <div className='grid h-screen grid-cols-1 overflow-hidden lg:grid-cols-[1.05fr_0.95fr]'>
            {/* ── 좌측 히어로 (임팩트·인터랙티브) ── */}
            <section
                onMouseMove={(e) => {
                    const r = e.currentTarget.getBoundingClientRect()
                    setTilt({
                        x: (e.clientX - r.left) / r.width - 0.5,
                        y: (e.clientY - r.top) / r.height - 0.5,
                    })
                }}
                onMouseLeave={() => setTilt({ x: 0, y: 0 })}
                className='bg-navy relative hidden flex-col justify-between overflow-hidden p-10 text-white lg:flex xl:p-14'>
                {/* 디지털 격자 배경(미세 드리프트 + 커서 패럴랙스) */}
                <div
                    className='pointer-events-none absolute inset-0 opacity-[0.13]'
                    style={{
                        backgroundImage:
                            'radial-gradient(rgba(255,255,255,.95) 1px, transparent 1.5px)',
                        backgroundSize: '30px 30px',
                        animation: 'hai-grid-pan 6s linear infinite',
                        transform: `translate(${tilt.x * -10}px, ${tilt.y * -10}px)`,
                    }}
                />
                <div
                    className='pointer-events-none absolute -top-28 -right-28 size-80 rounded-full border border-white/15 transition-transform duration-300 ease-out'
                    style={{
                        transform: `translate(${tilt.x * 26}px, ${tilt.y * 26}px)`,
                    }}
                />
                <div
                    className='pointer-events-none absolute -top-16 -right-16 size-56 rounded-full border border-white/10 transition-transform duration-300 ease-out'
                    style={{
                        transform: `translate(${tilt.x * 40}px, ${tilt.y * 40}px)`,
                    }}
                />

                <div className='animate-in fade-in slide-in-from-top-2 relative flex items-center gap-2.5 duration-500'>
                    <img
                        src='/brand/logo-white.svg'
                        alt=''
                        className='size-8'
                    />
                    <span className='text-lg font-bold tracking-tight'>
                        HACCP AI
                    </span>
                </div>

                <div className='animate-in fade-in slide-in-from-bottom-3 relative duration-700'>
                    <p className='text-xs font-bold tracking-[0.14em] text-white/70 uppercase'>
                        식품안전 인증 지원 서비스
                    </p>
                    <h1 className='mt-4 text-4xl leading-tight font-extrabold tracking-tight xl:text-5xl'>
                        HACCP 인증,
                        <br />
                        AI와 함께 한 곳에서
                    </h1>
                    <p className='mt-4 max-w-lg text-base text-balance text-white/80'>
                        대화형 진단 · 자동 채점 · AI 보고서. 인증 준비부터
                        유지까지 단계별로 안내합니다.
                    </p>
                    <LiveInspection />
                </div>

                <div className='relative text-xs tracking-wide text-white/55'>
                    PAI · HACCP AI
                </div>
            </section>

            {/* ── 우측 폼 ── */}
            <section className='bg-background flex items-center justify-center overflow-y-auto p-6'>
                <div className='animate-in fade-in w-full max-w-sm duration-500'>
                    {/* 모바일 브랜드(히어로 숨김 시) */}
                    <div className='mb-8 flex items-center gap-2 lg:hidden'>
                        <img
                            src='/brand/logo-icon.svg'
                            alt=''
                            className='size-9 rounded-lg'
                        />
                        <span className='text-foreground text-lg font-bold'>
                            HACCP AI
                        </span>
                    </div>

                    <h2 className='text-foreground text-2xl font-bold tracking-tight'>
                        로그인
                    </h2>
                    <p className='text-muted-foreground mt-1.5 text-sm'>
                        이메일로 로그인하거나 knock 계정을 사용하세요.
                    </p>

                    <form onSubmit={handleSelfLogin} className='mt-7 space-y-4'>
                        <div className='space-y-1.5'>
                            <label
                                className='text-foreground text-sm font-medium'
                                htmlFor='login-email'>
                                이메일
                            </label>
                            <Input
                                id='login-email'
                                type='email'
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete='email'
                                placeholder='name@example.com'
                                required
                            />
                        </div>
                        <div className='space-y-1.5'>
                            <label
                                className='text-foreground text-sm font-medium'
                                htmlFor='login-password'>
                                비밀번호
                            </label>
                            <div className='relative'>
                                <Input
                                    id='login-password'
                                    type={showPw ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) =>
                                        setPassword(e.target.value)
                                    }
                                    autoComplete='current-password'
                                    placeholder='비밀번호'
                                    className='pr-10'
                                    required
                                />
                                <button
                                    type='button'
                                    tabIndex={-1}
                                    onClick={() => setShowPw((v) => !v)}
                                    aria-label={
                                        showPw
                                            ? '비밀번호 숨기기'
                                            : '비밀번호 표시'
                                    }
                                    className='text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 grid w-10 place-items-center transition-colors'>
                                    {showPw ? (
                                        <EyeOff className='size-4' />
                                    ) : (
                                        <Eye className='size-4' />
                                    )}
                                </button>
                            </div>
                        </div>

                        {error ? <ErrorBanner>{error}</ErrorBanner> : null}

                        <Button
                            type='submit'
                            size='lg'
                            className='w-full'
                            disabled={loading}>
                            {loading ? '처리 중…' : '로그인'}
                        </Button>
                    </form>

                    <div className='text-muted-foreground my-5 flex items-center gap-3 text-xs'>
                        <span className='bg-border h-px flex-1' />
                        또는
                        <span className='bg-border h-px flex-1' />
                    </div>

                    <Button
                        type='button'
                        variant='ghost'
                        className='text-muted-foreground w-full font-normal'
                        disabled={loading}
                        onClick={handleKnokaLogin}>
                        knock 계정으로 로그인
                    </Button>

                    <p className='text-muted-foreground mt-6 text-center text-sm'>
                        계정이 없으신가요?{' '}
                        <Link
                            to='/signup'
                            className='text-primary font-medium hover:underline'>
                            회원가입 신청
                        </Link>
                    </p>
                </div>
            </section>

            <style>{`@keyframes hai-grid-pan{from{background-position:0 0}to{background-position:30px 30px}}`}</style>
        </div>
    )
}
